import { observable, computed, toJS } from 'mobx'
import { isObject } from 'lodash'
import Model from '../shared/model'
import { deepGet } from '../shared/utils'

import validate from '../shared/decorators/model/validate'

import { store as programStore } from '../program'
import { store as guidedTourStore } from '../guided-tour'

@validate({
  /* eslint-disable */
  // step general
  name: 'required|string|minLen:2',
  // 'address.street': 'string|minLen:2',
  // 'address.region': 'number|min:1',

  // information
  phone: 'required|string|regex:\\\+?[0-9\\\-]+$',
  // fax: 'string|minLen:2',
  website: 'required|url',
  email: 'required|email',
  description: 'required|string|minLen:100|maxLen:1000',

  // step visit
  // bayreuth-connection/musbi-frontend#82
  openingHours: 'required|string|minLen:10',
  // drivingInstructions: required|string|minLen:10
  entryFeeAdults: 'required|number|min:0',
  entryFeePupils: 'required|number|min:0',
  entryFeeGroups: 'required|number|min:0',
  minGroupSize: 'required|number|min:0',

  // equipments
  equipments: 'required|array|minLen:1',
  qs: 'required|array|minLen:1',

  // media
  images: 'required'
  /* eslint-enable */
})
export default class Museum extends Model {

  @observable name = ''

  @observable phone = ''

  @observable fax = ''

  @observable website = ''

  @observable email = ''

  @observable description = ''

  @observable openingHours = ''

  @observable drivingInstructions = ''

  @observable entryFeeAdults

  @observable entryFeePupils

  @observable entryFeeGroups

  @observable minGroupSize

  @observable isNew

  @observable equipments = []

  @observable qs = []

  @observable address

  @observable links = null

  @observable images = null

  @observable _message = {}

  @observable rejectedFields = []

  @observable dirty = true

  @observable programs = []

  @observable programsLoading = false

  @observable guidedTours = []

  @observable guidedToursLoading = false

  @computed get isReleaseable() {
    return this.isNew * 1 === 0
  }

  @computed get allFieldsRejected() {
    // just to detect changes
    // eslint-disable-next-line
    const len = this.rejectedFields.length
    const messages = this._message
    const dirty = typeof this.dirty === 'object'
      ? (this.dirty || {})
      : {}
    const dirtyKeys = Object.keys(dirty)
    const rejected = dirtyKeys
      .reduce((memo, key) => {
        return memo || !!messages[`museum-${key}`]
      }, false)
    return rejected
  }

  @computed get title() {
    return deepGet(this, 'name') || '-'
  }

  @computed get subtitle() {
    return deepGet(this, 'address.location') || '-'
  }

  @computed get isDirty() {
    return !!this.dirty
  }

  constructor(...args) {
    super(...args)

    this.dirtyFieldGroups = {
      address: ['name', 'address'],
      info: ['description', 'phone', 'fax', 'email', 'website'],
      contact: ['phone', 'fax', 'email', 'website'],
      openingHours: ['openingHours'],
      drivingInstructions: ['drivingInstructions'],
      entryFees: ['entryFeePupils', 'entryFeeGroups', 'entryFeeAdults', 'minGroupSize'],
      equipments: ['equipments', 'qs'],
      images: ['images'],
      links: ['links'],
      attend: [
        'openingHours',
        'drivingInstructions',
        'entryFeePupils',
        'entryFeeGroups',
        'entryFeeAdults',
        'minGroupSize'
      ]
    }

    this.initValidate()

    this.type = 'museum'
  }

  parseList(list) {
    return (list || []).map((itm) => ({
      id: itm.value || itm.id,
      value: itm.value || itm.id,
      amount: itm.pivot ? itm.pivot.amount : itm.attr,
      attr: itm.pivot ? itm.pivot.amount : itm.attr
    }))
  }

  // eslint-disable-next-line complexity
  parse(data) {

    data.address = {
      street: '',
      number: '',
      zip: '',
      location: '',
      region: '',
      latitude: '',
      longitude: '',
      ...this.address || {},
      ...data.address || {}
    }

    if (!data.address.region) {
      data.address.region = 1 // default to first region.
    }

    if (data.equipments) {
      data.equipments = this.parseList(data.equipments)
    }

    if (data.qs) {
      data.qs = this.parseList(data.qs)
    }

    if (data.images && !Array.isArray(data.images)) {
      data.images = [data.images]
      data.images = data.images.map((image) => {
        image.banner = image.type === 'banner'
        return image
      })
    }

    if (data.links && !Array.isArray(data.links)) {
      data.links = [data.links]
    }

    data._message = {
      
      ...this._message || {},
      ...data._message || {},
    }

    data.rejectedFields = Object.keys(data._message)
      .filter((key) => !!data._message[key])

    return data
  }

  activate() {
    this.set({
      isDeactivated: false
    })
    return this.save()
  }

  deactivate() {
    this.set({
      isDeactivated: true
    })
    return this.save()
  }

  clone(data = {}) {
    let links
    let images
    if ('links' in data) {
      links = data.links
      delete data.links
    }
    if ('images' in data) {
      images = data.images
      delete data.images
    }
    const clone = super.clone(data)
    if (links) {
      clone.links = links
    }
    if (images) {
      clone.images = images
    }
    return clone
  }

  set(data) {

    super.set(data)

    // if (!this.id) {
    //   const rawData = {
    //     ...this.getJSON(),
    //     links: null,
    //     images: null
    //   }
    //   localStorage.museumDraft = JSON.stringify(rawData)
    // }

  }

  save(opts = {}) {
    opts.action = 'upload'
    opts.method = 'post'
    if (!this.id) {
      opts.id = 'new'
    }
    opts.contentType = 'form-data'
    return super.save(opts)
      .then((res) => {
        // delete localStorage.museumDraft
        return res
      })
  }

  @computed get asJSON() {

    return {
      id: this.id,
      name: this.name,
      phone: this.phone,
      fax: this.fax,
      website: this.website,
      email: this.email,
      description: this.description,
      openingHours: this.openingHours,
      drivingInstructions: this.drivingInstructions,
      entryFeeAdults: this.entryFeeAdults,
      entryFeePupils: this.entryFeePupils,
      entryFeeGroups: this.entryFeeGroups,
      minGroupSize: this.minGroupSize,
      isNew: this.isNew,
      isDeactivated: this.isDeactivated,
      address: this.address,
      equipments: toJS(this.equipments),
      qs: toJS(this.qs),
      links: toJS(this.links),
      images: toJS(this.images),
      _message: this._message,
    }

  }

  getJSON() {
    return this.asJSON
  }

  isFieldGroupDirty(fieldGroup) {
    const fields = this.dirtyFieldGroups[fieldGroup]
    let isDirty = false
    let i
    const len = fields.length
    const dirtyFields = this.dirty

    if (
      !(isObject(dirtyFields)
        || Array.isArray(dirtyFields))
    ) {
      return dirtyFields
    }

    for (i = 0; i < len; i += 1) {
      if (
        dirtyFields[fields[i]] === true
        || isObject(dirtyFields[fields[i]])
      ) {
        isDirty = true
      }
    }

    return isDirty
  }

  matches(filter) {
    if (!filter.search) {
      return true
    }

    const search = filter.search.toLowerCase()

    return `${
      this.name
    } ${
      this.description
    }`.toLowerCase().indexOf(search) > -1
  }

  loadOffers() {
    this.programsLoading = true
    this.guidedToursLoading = true
    const museum = this

    return Promise.all([
      programStore.where({ museumId: this.id })
        .catch((ex) => {
          museum.programsLoading = false
          throw ex
        })
        .then(({ models }) => {
          museum.programs = models
          museum.programsLoading = false
        }),
      guidedTourStore.where({ museumId: this.id })
        .catch((ex) => {
          museum.guidedToursLoading = false
          throw ex
        })
        .then(({ models }) => {
          museum.guidedTours = models
          museum.guidedToursLoading = false
        })
    ])

  }

}



// WEBPACK FOOTER //
// ./src/museum/model.js